import Button from '@rio-cloud/rio-uikit/Button';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import ExpanderPanel from '@rio-cloud/rio-uikit/ExpanderPanel';
import { useEffect, useRef, useState } from 'react';

import { type SnapshotReceipt, usePublishSnapshotMutation } from '../../services/scmMockApi';
import { type DemoScenario, newExampleScenario, parseDemoScenario } from './scenarioImport';
import { SM, useScenarioLabel } from './scenarioMessages';

export default function ImportDemoScenario({
    disabled,
    onBusyChange,
    onDataChanged,
}: {
    disabled: boolean;
    onBusyChange: (busy: boolean) => void;
    onDataChanged: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [scenario, setScenario] = useState<DemoScenario>();
    const [receipts, setReceipts] = useState<SnapshotReceipt[]>([]);
    const [error, setError] = useState<'auth' | 'failed'>();
    const [formatError, setFormatError] = useState('');
    const [detail, setDetail] = useState('');
    const [busy, setBusy] = useState(false);
    const inFlight = useRef(false);
    const mounted = useRef(true);
    const [send] = usePublishSnapshotMutation();
    const label = useScenarioLabel();
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    const review = () => {
        try {
            setScenario(parseDemoScenario(text));
            setReceipts([]);
            setError(undefined);
            setFormatError('');
        } catch (failure) {
            setFormatError(failure instanceof Error ? failure.message : 'Invalid JSON.');
        }
    };
    const publish = async () => {
        if (!scenario || disabled || inFlight.current) {
            return;
        }
        inFlight.current = true;
        setBusy(true);
        onBusyChange(true);
        setError(undefined);
        setDetail('');
        try {
            for (const entry of scenario.entries.slice(receipts.length)) {
                if (!mounted.current) {
                    break;
                }
                const receipt = await send(entry).unwrap();
                if (mounted.current) {
                    setReceipts(previous => [...previous, receipt]);
                }
                onDataChanged();
            }
        } catch (failure) {
            const status =
                typeof failure === 'object' && failure !== null && 'status' in failure ? failure.status : undefined;
            if (mounted.current) {
                setError(status === 401 || status === 403 ? 'auth' : 'failed');
                if (status === 400 && typeof failure === 'object' && failure !== null && 'data' in failure) {
                    const data = failure.data;
                    if (
                        typeof data === 'object' &&
                        data !== null &&
                        'detail' in data &&
                        typeof data.detail === 'string'
                    ) {
                        setDetail(data.detail.slice(0, 1000));
                    }
                }
            }
        } finally {
            inFlight.current = false;
            if (mounted.current) {
                setBusy(false);
            }
            onBusyChange(false);
        }
    };
    const total = scenario?.entries.length ?? 0;
    const complete = total > 0 && receipts.length === total;
    return (
        <>
            <Button disabled={disabled} onClick={() => setOpen(true)}>
                <SM name='title' />
            </Button>
            <Dialog
                show={open}
                title={<SM name='title' />}
                bsSize='lg'
                useOverflow
                disableEsc={busy}
                onCloseValidation={() => !busy}
                showCloseButton={!busy}
                onClose={() => {
                    if (!busy) {
                        setOpen(false);
                    }
                }}
                body={
                    <div className='display-grid gap-15'>
                        <p>
                            <SM name='intro' />
                        </p>
                        <div className='text-medium word-break-all'>
                            <SM
                                name='environment'
                                values={{ url: import.meta.env.VITE_SCI_API_BASE_URL || window.location.origin }}
                            />
                        </div>
                        <div className='alert alert-info margin-bottom-0'>
                            <SM name='replacement' />
                        </div>
                        {!scenario ? (
                            <>
                                <label htmlFor='demo-scenario-json'>
                                    <SM name='json' />
                                </label>
                                <Button
                                    onClick={() => {
                                        setText(newExampleScenario());
                                        setFormatError('');
                                    }}
                                >
                                    <SM name='example' />
                                </Button>
                                <textarea
                                    id='demo-scenario-json'
                                    className='form-control'
                                    rows={12}
                                    value={text}
                                    onChange={event => setText(event.target.value)}
                                    spellCheck={false}
                                />
                                {formatError && (
                                    <div role='alert'>
                                        <SM name='invalid' values={{ detail: formatError }} />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <h3 className='margin-0 word-break-all'>{scenario.name}</h3>
                                <ExpanderPanel title={label('json')} bsStyle='separator'>
                                    <textarea
                                        className='form-control text-size-12'
                                        aria-label={label('json')}
                                        rows={12}
                                        value={text}
                                        readOnly
                                        spellCheck={false}
                                    />
                                </ExpanderPanel>
                                <div>
                                    <SM
                                        name='requests'
                                        values={{
                                            count: scenario.entries.filter(
                                                entry => entry.resource === 'transport-requests'
                                            ).length,
                                        }}
                                    />{' '}
                                    ·{' '}
                                    <SM
                                        name='transports'
                                        values={{
                                            count: scenario.entries.filter(entry => entry.resource === 'transports')
                                                .length,
                                        }}
                                    />
                                </div>
                                <p>
                                    <SM name='order' />
                                </p>
                                <div className='table-responsive'>
                                    <table className='table table-condensed'>
                                        <thead>
                                            <tr>
                                                <th>
                                                    <SM name='resource' />
                                                </th>
                                                <th>
                                                    <SM name='id' />
                                                </th>
                                                <th>
                                                    <SM name='status' />
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {scenario.entries.map((entry, index) => (
                                                <tr key={`${entry.resource}:${entry.id}`}>
                                                    <td>
                                                        <SM
                                                            name={
                                                                entry.resource === 'transport-requests'
                                                                    ? 'request'
                                                                    : 'transport'
                                                            }
                                                        />
                                                    </td>
                                                    <td className='word-break-all'>{entry.id}</td>
                                                    <td>
                                                        {receipts[index] ? (
                                                            <SM
                                                                name='acknowledged'
                                                                values={{ revision: receipts[index].revision }}
                                                            />
                                                        ) : (
                                                            <SM name='pending' />
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div role='status' aria-live='polite'>
                                    <SM
                                        name={complete ? 'complete' : 'progress'}
                                        values={{ done: receipts.length, total }}
                                    />
                                </div>
                                <p>
                                    <SM name='projection' />
                                </p>
                                <p>
                                    <SM name='keepOpen' />
                                </p>
                                {error && (
                                    <div role='alert' className='alert alert-danger'>
                                        <SM name={error} />
                                        {detail && <div>{detail}</div>}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                }
                footer={
                    <div className='display-flex flex-wrap justify-content-end gap-10'>
                        <Button disabled={busy} onClick={() => setOpen(false)}>
                            <SM name='close' />
                        </Button>
                        {!scenario ? (
                            <Button bsStyle={Button.PRIMARY} disabled={!text.trim()} onClick={review}>
                                <SM name='review' />
                            </Button>
                        ) : (
                            <>
                                <Button
                                    disabled={busy}
                                    onClick={() => {
                                        setScenario(undefined);
                                        setError(undefined);
                                    }}
                                >
                                    <SM name={receipts.length ? 'newBatch' : 'edit'} />
                                </Button>
                                <Button
                                    bsStyle={Button.PRIMARY}
                                    disabled={busy || complete || disabled}
                                    onClick={publish}
                                >
                                    <SM
                                        name={busy ? 'publishing' : 'publish'}
                                        values={{ count: total - receipts.length }}
                                    />
                                </Button>
                            </>
                        )}
                    </div>
                }
            />
        </>
    );
}
